import {createApi} from '@reduxjs/toolkit/query/react'
import type {
  NotificationsResponse,
  NotificationsQuery,
  NotificationSummaryResponse,
  NotificationScopeQuery,
  UnreadCountResponse,
  EVerifyBannerResponse,
  Notification,
} from '../../models/notification'
import {baseQuery} from '../base-query'

interface RawNotification extends Omit<Notification, 'id'> {
  _id?: string
  id?: string
}

function normalizeNotification(raw: RawNotification): Notification {
  return {
    ...raw,
    id: raw.id || raw._id || '',
  }
}

// Every list/summary entry provides 'Notifications' whatever its category, and every mutation
// invalidates it plus 'UnreadCount', so a read or dismiss on the inbox, the Work Authorization
// page or the bell refetches the other two.
export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  baseQuery: baseQuery,
  tagTypes: ['Notifications', 'UnreadCount'],
  endpoints: (builder) => ({
    getNotifications: builder.query<NotificationsResponse, NotificationsQuery>({
      providesTags: ['Notifications'],
      query: ({page = 1, limit = 20, isRead, type, search, category}) => ({
        url: '/notifications',
        method: 'GET',
        params: {
          page,
          limit,
          ...(isRead !== undefined && {isRead}),
          ...(type && type !== 'all' && {type}),
          ...(search && {search}),
          ...(category && {category}),
        },
      }),
      transformResponse: (response: {
        data: RawNotification[]
        total: number
        page: number
        limit: number
      }): NotificationsResponse => ({
        ...response,
        data: response.data.map(normalizeNotification),
      }),
    }),

    getNotificationsSummary: builder.query<
      NotificationSummaryResponse,
      NotificationScopeQuery | undefined
    >({
      providesTags: ['Notifications'],
      query: (scope) => ({
        url: '/notifications/summary',
        method: 'GET',
        ...(scope?.category && {params: {category: scope.category}}),
      }),
    }),

    getUnreadCount: builder.query<UnreadCountResponse, undefined>({
      providesTags: ['UnreadCount'],
      query: () => ({
        url: '/notifications/unread-count',
        method: 'GET',
      }),
    }),

    markAsRead: builder.mutation<Notification, string>({
      invalidatesTags: ['Notifications', 'UnreadCount'],
      query: (id) => ({
        url: `/notifications/${id}/read`,
        method: 'PATCH',
      }),
      transformResponse: (response: RawNotification): Notification =>
        normalizeNotification(response),
    }),

    markAsUnread: builder.mutation<Notification, string>({
      invalidatesTags: ['Notifications', 'UnreadCount'],
      query: (id) => ({
        url: `/notifications/${id}/unread`,
        method: 'PATCH',
      }),
      transformResponse: (response: RawNotification): Notification =>
        normalizeNotification(response),
    }),

    markAllAsRead: builder.mutation<{updatedCount: number}, NotificationScopeQuery | undefined>({
      invalidatesTags: ['Notifications', 'UnreadCount'],
      query: (scope) => ({
        url: '/notifications/read-all',
        method: 'PATCH',
        ...(scope?.category && {params: {category: scope.category}}),
      }),
    }),

    dismissNotification: builder.mutation<Notification, string>({
      invalidatesTags: ['Notifications', 'UnreadCount'],
      query: (id) => ({
        url: `/notifications/${id}/dismiss`,
        method: 'PATCH',
      }),
      transformResponse: (response: RawNotification): Notification =>
        normalizeNotification(response),
    }),

    getEVerifyBanner: builder.query<EVerifyBannerResponse, string>({
      query: (applicantId) => ({
        url: `/notifications/everify-banner/${applicantId}`,
        method: 'GET',
      }),
    }),
  }),
})

export const {
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  useGetNotificationsSummaryQuery,
  useGetUnreadCountQuery,
  useMarkAsReadMutation,
  useMarkAsUnreadMutation,
  useMarkAllAsReadMutation,
  useDismissNotificationMutation,
  useGetEVerifyBannerQuery,
} = notificationsApi
